import { makeStyles, Text } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { useRoutingContext } from 'app/routing_context'
import { t } from 'app/i18l'
import React, { useEffect, useState } from 'react'
import { Alert, Modal, Platform, Pressable, View } from 'react-native'
import { useStoredState } from 'shared/hooks/stored_state'
import { pickImageFromLibrary } from 'shared/image_picker'
import { useUser } from 'users/queries/detail'

export default function UploadProfilePhotoModal({
  onAvatarChange,
}: {
  onAvatarChange: (avatar: string, mimeType: string) => Promise<void>
}) {
  const styles = useStyles()
  const [modalVisible, setModalVisible] = useState(false)
  const { data: user } = useUser()
  const { handleNavigation } = useRoutingContext()
  const hasProfilePhoto = user?.hasProfilePhoto

  const [hideUploadProfilePhotoModal, setHideUploadProfilePhotoModal] =
    useStoredState<boolean>('show_upload_profile_photo_modal')

  const uploadImage = async () => {
    const pickImageResult = await pickImageFromLibrary()
    if (pickImageResult.status === 'success') {
      handleNavigation({
        routeName: 'account',
      })
      await onAvatarChange(
        pickImageResult.files[0].uri,
        pickImageResult.files[0].mimeType
      )
    }
  }

  useEffect(() => {
    if (
      hasProfilePhoto === false &&
      hideUploadProfilePhotoModal === null &&
      Platform.OS !== 'web' &&
      onlineManager.isOnline()
    ) {
      setModalVisible(true)
    }
  }, [hasProfilePhoto, hideUploadProfilePhotoModal])

  return (
    <View>
      <Modal animationType="slide" transparent={true} visible={modalVisible}>
        <View style={styles.container}>
          <View style={styles.modalView}>
            <Text style={styles.modalText}>{t('uploadPhotoModalText')}</Text>
            <View style={styles.linksContainer}>
              <Pressable
                onPress={() => {
                  setHideUploadProfilePhotoModal(true)
                  uploadImage().catch(e => null)
                  setModalVisible(!modalVisible)
                }}
              >
                <Text style={styles.link}>{t('choosePhoto')}</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  setHideUploadProfilePhotoModal(true)
                  setModalVisible(!modalVisible)
                  Alert.alert(t('uploadPhotoAlertTitle'), t('uploadPhotoAlert'))
                }}
              >
                <Text style={styles.link}>{t('notNow')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalView: {
    margin: 10,
    backgroundColor: theme.colors?.white,
    borderRadius: 20,
    padding: 25,
    alignItems: 'center',
    shadowColor: theme.colors?.black,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalText: {
    marginBottom: 15,
    textAlign: 'center',
    fontSize: 18,
    color: theme.colors?.black,
  },
  linksContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  link: {
    color: theme.colors?.blue,
    textDecorationLine: 'underline',
    marginHorizontal: 10,
    fontSize: 16,
  },
}))
